import type { ExerciseDef } from './types'

// ─── 6.1 Linear regression ────────────────────────────────────────────────────

export const handFit: ExerciseDef = {
  starter: `// 30 noisy points scattered around a hidden line.
const d = data.line(30, 2, 1, 0.5)

// 1. mse(xs, ys, w, b): mean squared error of the line ŷ = w·x + b
function mse(xs: number[], ys: number[], w: number, b: number): number {
  return 0
}

// 2. Be the learning algorithm: pick w and b that bring the error below 0.35.
//    Run, look at the plot, adjust, repeat.
const w = 0.5
const b = 0

plot.scatter(d.x.map((x, i) => [x, d.y[i]]), { title: "the data" })
plot.fn(x => w * x + b, [-3, 3], { title: \`your line: y = \${w}x + \${b}\` })
console.log("MSE:", mse(d.x, d.y, w, b))
`,
  solution: `const d = data.line(30, 2, 1, 0.5)

function mse(xs: number[], ys: number[], w: number, b: number): number {
  let total = 0
  for (let i = 0; i < xs.length; i++) total += (w * xs[i] + b - ys[i]) ** 2
  return total / xs.length
}

const w = 2
const b = 1

plot.scatter(d.x.map((x, i) => [x, d.y[i]]), { title: "the data" })
plot.fn(x => w * x + b, [-3, 3], { title: \`your line: y = \${w}x + \${b}\` })
console.log("MSE:", mse(d.x, d.y, w, b))
`,
  tests: `
test("mse is 0 for a perfect fit", () => expect(mse([1, 2, 3], [3, 5, 7], 2, 1), "mse([1,2,3], [3,5,7], 2, 1)").toBeCloseTo(0))
test("mse averages the squared errors", () => expect(mse([0, 1], [1, 1], 0, 0), "mse([0,1], [1,1], 0, 0)").toBeCloseTo(1))
test("your line fits the data with MSE below 0.35", () => {
  random.seed(42)
  const __d = data.line(30, 2, 1, 0.5)
  expect(mse(__d.x, __d.y, w, b), "MSE of your line").toBeLessThan(0.35)
})
`,
}

// ─── 6.2 The training loop ────────────────────────────────────────────────────

export const trainLine: ExerciseDef = {
  starter: `// Learn w and b by gradient descent on the mean squared error.
//   prediction  ŷᵢ = w·xᵢ + b
//   ∂L/∂w = (2/n) Σ (ŷᵢ − yᵢ)·xᵢ
//   ∂L/∂b = (2/n) Σ (ŷᵢ − yᵢ)
function train(xs: number[], ys: number[], lr: number, epochs: number): { w: number; b: number; losses: number[] } {
  let w = 0
  let b = 0
  const losses: number[] = []
  for (let epoch = 0; epoch < epochs; epoch++) {
    // 1. compute the gradients (and the loss, to plot)
    // 2. take a step: w -= lr * gradW, b -= lr * gradB
  }
  return { w, b, losses }
}

const d = data.line(100, 3, -2, 0.3)
const result = train(d.x, d.y, 0.05, 300)
console.log("learned w =", result.w, " b =", result.b, "  (true: 3 and −2)")
plot.line(result.losses, { title: "training loss" })
`,
  solution: `function train(xs: number[], ys: number[], lr: number, epochs: number): { w: number; b: number; losses: number[] } {
  let w = 0
  let b = 0
  const n = xs.length
  const losses: number[] = []
  for (let epoch = 0; epoch < epochs; epoch++) {
    let gradW = 0
    let gradB = 0
    let loss = 0
    for (let i = 0; i < n; i++) {
      const err = w * xs[i] + b - ys[i]
      loss += err * err / n
      gradW += (2 / n) * err * xs[i]
      gradB += (2 / n) * err
    }
    losses.push(loss)
    w -= lr * gradW
    b -= lr * gradB
  }
  return { w, b, losses }
}

const d = data.line(100, 3, -2, 0.3)
const result = train(d.x, d.y, 0.05, 300)
console.log("learned w =", result.w, " b =", result.b, "  (true: 3 and −2)")
plot.line(result.losses, { title: "training loss" })
`,
  tests: `
test("learns w ≈ 3", () => {
  random.seed(7)
  const __d = data.line(100, 3, -2, 0.3)
  expect(train(__d.x, __d.y, 0.05, 300).w, "w").toBeCloseTo(3, 0.1)
})
test("learns b ≈ −2", () => {
  random.seed(7)
  const __d = data.line(100, 3, -2, 0.3)
  expect(train(__d.x, __d.y, 0.05, 300).b, "b").toBeCloseTo(-2, 0.15)
})
test("records one loss per epoch, and it goes down", () => {
  const r = train([0, 1, 2, 3], [1, 3, 5, 7], 0.05, 50)
  expect(r.losses, "losses").toHaveLength(50)
  expect(r.losses[49], "final loss").toBeLessThan(r.losses[0])
})
`,
}

export const sgdExample: ExerciseDef = {
  starter: `// Stochastic gradient descent: estimate the gradient from a small random
// "mini-batch" instead of the whole dataset. Noisier, but far cheaper per step.
const d = data.line(2000, 3, -2, 0.3)
let w = 0, b = 0
const lr = 0.05, batch = 16
const chart = plot.live("loss on each mini-batch")

for (let step = 0; step < 400; step++) {
  let gw = 0, gb = 0, loss = 0
  for (let k = 0; k < batch; k++) {
    const i = random.int(0, d.x.length)
    const err = w * d.x[i] + b - d.y[i]
    gw += (2 / batch) * err * d.x[i]
    gb += (2 / batch) * err
    loss += err * err / batch
  }
  w -= lr * gw
  b -= lr * gb
  chart.push(loss)
}
console.log("w ≈", round(w, 3), " b ≈", round(b, 3))
`,
}

// ─── 6.3 Logistic regression ──────────────────────────────────────────────────

export const sigmoidPlot: ExerciseDef = {
  starter: `const sigmoid = (z: number) => 1 / (1 + Math.exp(-z))
plot.fn(sigmoid, [-8, 8], { title: "σ(z) squashes any number into (0, 1)" })
console.log(sigmoid(-100), sigmoid(0), sigmoid(100))
`,
}

export const trainLogistic: ExerciseDef = {
  starter: `// Train a classifier on two clusters of 2-D points.
// Model:  p = σ(w₀·x₀ + w₁·x₁ + b)        Loss: binary cross-entropy
// The beautiful gradient: ∂L/∂z = p − y, so
//   ∂L/∂wⱼ = mean of (p − y)·xⱼ     ∂L/∂b = mean of (p − y)
const sigmoid = (z: number) => 1 / (1 + Math.exp(-z))

function trainLogistic(X: number[][], y: number[], lr: number, epochs: number) {
  let w = [0, 0]
  let b = 0
  for (let epoch = 0; epoch < epochs; epoch++) {
    // compute gradients over every example, then update w and b
  }
  return { w, b }
}

function accuracy(X: number[][], y: number[], model: { w: number[]; b: number }): number {
  let right = 0
  for (let i = 0; i < X.length; i++) {
    const p = sigmoid(vec.dot(model.w, X[i]) + model.b)
    if ((p >= 0.5 ? 1 : 0) === y[i]) right++
  }
  return right / X.length
}

const d = data.blobs(200, 2, 0.5)
const model = trainLogistic(d.X, d.y, 0.5, 200)
console.log("accuracy:", accuracy(d.X, d.y, model), model)
plot.scatter(d.X, { labels: d.y, title: "two classes" })
`,
  solution: `const sigmoid = (z: number) => 1 / (1 + Math.exp(-z))

function trainLogistic(X: number[][], y: number[], lr: number, epochs: number) {
  let w = [0, 0]
  let b = 0
  const n = X.length
  for (let epoch = 0; epoch < epochs; epoch++) {
    const gw = [0, 0]
    let gb = 0
    for (let i = 0; i < n; i++) {
      const p = sigmoid(vec.dot(w, X[i]) + b)
      const e = p - y[i]
      gw[0] += (e * X[i][0]) / n
      gw[1] += (e * X[i][1]) / n
      gb += e / n
    }
    w = [w[0] - lr * gw[0], w[1] - lr * gw[1]]
    b -= lr * gb
  }
  return { w, b }
}

function accuracy(X: number[][], y: number[], model: { w: number[]; b: number }): number {
  let right = 0
  for (let i = 0; i < X.length; i++) {
    const p = sigmoid(vec.dot(model.w, X[i]) + model.b)
    if ((p >= 0.5 ? 1 : 0) === y[i]) right++
  }
  return right / X.length
}

const d = data.blobs(200, 2, 0.5)
const model = trainLogistic(d.X, d.y, 0.5, 200)
console.log("accuracy:", accuracy(d.X, d.y, model), model)
plot.scatter(d.X, { labels: d.y, title: "two classes" })
`,
  tests: `
test("classifies at least 95% of points correctly", () => {
  random.seed(3)
  const __d = data.blobs(200, 2, 0.5)
  const __m = trainLogistic(__d.X, __d.y, 0.5, 200)
  expect(accuracy(__d.X, __d.y, __m), "accuracy").toBeGreaterThan(0.95)
})
test("the weights actually moved", () => {
  const __m = trainLogistic([[1, 0], [-1, 0]], [1, 0], 0.5, 50)
  expect(__m.w[0], "w[0] for a simple left/right split").toBeGreaterThan(0.5)
})
`,
}

// ─── 6.4 Generalisation ───────────────────────────────────────────────────────

export const memorizer: ExerciseDef = {
  starter: `// A "model" that just memorises: for a new x, return the y of the closest training point.
random.seed(1)
const all = data.line(60, 2, 1, 1.0)
const trainX = all.x.slice(0, 30), trainY = all.y.slice(0, 30)
const testX = all.x.slice(30), testY = all.y.slice(30)

const memorize = (x: number) => {
  let best = 0
  for (let i = 1; i < trainX.length; i++) if (Math.abs(trainX[i] - x) < Math.abs(trainX[best] - x)) best = i
  return trainY[best]
}
const line = (x: number) => 2 * x + 1

const mse = (f: (x: number) => number, xs: number[], ys: number[]) => vec.mean(xs.map((x, i) => (f(x) - ys[i]) ** 2))
console.log("memoriser — train error:", round(mse(memorize, trainX, trainY), 3), "  test error:", round(mse(memorize, testX, testY), 3))
console.log("simple line — train error:", round(mse(line, trainX, trainY), 3), "  test error:", round(mse(line, testX, testY), 3))
`,
}

export const split: ExerciseDef = {
  starter: `// trainTestSplit(items, testFraction): shuffle, then put the last
// testFraction of them in "test" and the rest in "train".
// (random.shuffle(items) returns a shuffled copy.)
function trainTestSplit<T>(items: T[], testFraction: number): { train: T[]; test: T[] } {
  return { train: items, test: [] }
}

const { train, test } = trainTestSplit(range(10), 0.2)
console.log("train:", train, " test:", test)
`,
  solution: `function trainTestSplit<T>(items: T[], testFraction: number): { train: T[]; test: T[] } {
  const shuffled = random.shuffle(items)
  const nTest = Math.round(items.length * testFraction)
  return { train: shuffled.slice(0, items.length - nTest), test: shuffled.slice(items.length - nTest) }
}

const { train, test } = trainTestSplit(range(10), 0.2)
console.log("train:", train, " test:", test)
`,
  tests: `
test("20% of 10 items go to test", () => {
  const s = trainTestSplit(range(10), 0.2)
  expect(s.test, "test").toHaveLength(2)
  expect(s.train, "train").toHaveLength(8)
})
test("every item ends up in exactly one set", () => {
  const s = trainTestSplit(range(50), 0.3)
  expect([...s.train, ...s.test].sort((a, b) => a - b), "train + test, sorted").toEqual(range(50))
})
test("the split is shuffled, not just the last items", () => {
  const s = trainTestSplit(range(100), 0.5)
  expect(s.test.some(v => v < 50), "some test item from the first half").toBeTruthy()
})
`,
}

// ─── 6.5 Clustering ───────────────────────────────────────────────────────────

export const kmeans: ExerciseDef = {
  starter: `type P = number[]
const dist2 = (a: P, b: P) => (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2

// 1. assign(points, centroids): for each point, the index of its nearest centroid.
function assign(points: P[], centroids: P[]): number[] {
  return points.map(() => 0)
}

// 2. update(points, labels, k): each new centroid is the mean of its points.
//    (If a cluster is empty, put its centroid at [0, 0].)
function update(points: P[], labels: number[], k: number): P[] {
  return range(k).map(() => [0, 0])
}

// The full algorithm, using your two pieces:
function kmeansFit(points: P[], k: number, rounds = 10) {
  let centroids = random.shuffle(points).slice(0, k)
  let labels = assign(points, centroids)
  for (let r = 0; r < rounds; r++) {
    centroids = update(points, labels, k)
    labels = assign(points, centroids)
  }
  return { centroids, labels }
}

const d = data.blobs(150, 3, 0.35)
const fit = kmeansFit(d.X, 3)
plot.scatter(d.X, { labels: fit.labels, title: "k-means found these groups" })
`,
  solution: `type P = number[]
const dist2 = (a: P, b: P) => (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2

function assign(points: P[], centroids: P[]): number[] {
  return points.map(p => {
    let best = 0
    for (let c = 1; c < centroids.length; c++) if (dist2(p, centroids[c]) < dist2(p, centroids[best])) best = c
    return best
  })
}

function update(points: P[], labels: number[], k: number): P[] {
  return range(k).map(c => {
    const mine = points.filter((_, i) => labels[i] === c)
    if (mine.length === 0) return [0, 0]
    return [vec.mean(mine.map(p => p[0])), vec.mean(mine.map(p => p[1]))]
  })
}

function kmeansFit(points: P[], k: number, rounds = 10) {
  let centroids = random.shuffle(points).slice(0, k)
  let labels = assign(points, centroids)
  for (let r = 0; r < rounds; r++) {
    centroids = update(points, labels, k)
    labels = assign(points, centroids)
  }
  return { centroids, labels }
}

const d = data.blobs(150, 3, 0.35)
const fit = kmeansFit(d.X, 3)
plot.scatter(d.X, { labels: fit.labels, title: "k-means found these groups" })
`,
  tests: `
test("assign picks the nearest centroid", () => expect(assign([[0, 0], [10, 10], [9, 8]], [[1, 1], [10, 9]]), "assign(...)").toEqual([0, 1, 1]))
test("update averages each cluster", () => expect(update([[0, 0], [2, 0], [10, 10]], [0, 0, 1], 2), "update(...)").toEqual([[1, 0], [10, 10]]))
test("an empty cluster goes to [0, 0]", () => expect(update([[5, 5]], [0], 2)[1], "the empty cluster's centroid").toEqual([0, 0]))
test("finds three well-separated groups", () => {
  random.seed(11)
  const __d = data.blobs(150, 3, 0.3)
  const __f = kmeansFit(__d.X, 3, 15)
  // every true group should map to a single found cluster
  const ok = [0, 1, 2].every(g => new Set(__f.labels.filter((_, i) => __d.y[i] === g)).size === 1)
  expect(ok, "each true group lands in one cluster").toBeTruthy()
})
`,
}
